<script setup lang="ts">
import {onBeforeMount, ref, Ref, useTemplateRef} from "vue";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import EditViewSkeleton from "@/components/skeleton/EditViewSkeleton.vue";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import ReferenceGridFieldWidgetUtil from "@/utils/editView/widgets/ReferenceGridFieldWidgetUtil.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import SaveBehavior from "@/utils/constants/SaveBehavior.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import {QuickCreateProps} from "@/utils/presenters/interfaces.ts";
import EditViewLayout from "@/components/EditView/EditViewLayout.vue";
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import EntityReferenceData = namaTypes.EntityReferenceData;


let props = defineProps<QuickCreateProps>();
const currentPageIndex: Ref<number> = ref(0);
let util: ReferenceFieldWidgetUtil | ReferenceGridFieldWidgetUtil | undefined = undefined;
if (props.fieldUtil)
  util = props.fieldUtil as ReferenceFieldWidgetUtil | ReferenceGridFieldWidgetUtil;
const layoutFetched: Ref<boolean> = ref(false);
const object: Ref<BaseEntityDTO | undefined> = ref(props.newObject);
const requesterType: string | undefined = util ? util.presenter.entityType : props.requesterType;
const entityType: string | undefined = util ? util.referencedEntityType.value : props.newObject?.entityType;
const recordId: string | undefined = util ? (util.fetchFieldValue() ? util.fetchFieldValue().id : '') : props.newObject?.id;
const editViewLayoutRef = useTemplateRef<InstanceType<typeof EditViewLayout>>("editViewLayoutRef");

let quickCreatePlace: EditPlace = new EditPlace();
quickCreatePlace.entityType = entityType ?? '';
quickCreatePlace.view = ViewMode.NEW;
quickCreatePlace.tabId = props.tabId;
quickCreatePlace.quickCreateRequester = requesterType;
quickCreatePlace.quickCreate = true;
let presenter: EditPresenter = new EditPresenter(quickCreatePlace);
if (util?.presenter.closeQuickEditDialog)
  presenter.closeQuickEditDialog = util?.presenter.closeQuickEditDialog
if (props.parentFieldTitle)
  presenter.parentFieldTitle += props.parentFieldTitle;
onBeforeMount(async () => {
  // TODO :: ClientCache.NOTisOpenInPopup
  if (props.isEditRecord) {
    let result = await NamaCommon.namaFetch(NamaURLS.FIND_BY_ID, {
      params: {
        entityType: entityType,
        id: recordId
      }
    });
    if (result && NamaCommon.isSuccess(result)) {
      object.value = result.data;
    }
  }
  if (object.value && object.value.commitedBefore) {
    presenter.place.view = ViewMode.EDIT;
    presenter.place.id = object.value.id;
    presenter.place.createdObjectId = object.value.id;
  }
  presenter.createdObject = object.value;
  quickCreatePlace.quickCreateContextInfo = util ? util.presenter.createSearchContextInfo(util.fieldId()) : null;
  await presenter.start();
  presenter.afterFetchListener = props.afterFetchCallback;
  layoutFetched.value = true
});

async function saveRecordAndSelectedIfNeeded() {
  if (props.isEditRecord) {
    let saveSucceeded = await presenter.doSave(SaveBehavior.SaveAndCommit, true, '', false);
    if (!saveSucceeded)
      return false;
    if (props.isFieldEnabled)
      await setSelectedRef();
  } else {
    let saveSucceeded = await presenter.doSave(SaveBehavior.SaveAndCommit, false, '', false);
    if (!saveSucceeded)
      return false;
    if (props.isFieldEnabled)
      await setSelectedRef();
  }
  return true;
}

async function leaveQuickCreate() {
  return await presenter.confirmNavigateAway();
}

async function setSelectedRef() {
  if (util) {
    let entityRef: EntityReferenceData = {
      id: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.ID),
      entityType: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.ENTITY_TYPE),
      code: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.CODE),
      actualCode: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.ACTUAL_CODE),
      altCode: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.ALT_CODE),
      name1: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.NAME1),
      name2: NamaCommon.getFieldValue(presenter.fetchRecord(), CommonFieldIds.NAME2)
    }
    await util.onSelect(entityRef);
    await util.saveChanges(true);
  }
}

defineExpose({saveRecordAndSelectedIfNeeded, leaveQuickCreate, editViewLayoutRef});
</script>

<template>
  <div v-if="presenter.validLayout && layoutFetched" class="tw-h-[inherit] tw-w-full">
    <EditViewLayout ref="editViewLayoutRef" :presenter="presenter" :place="quickCreatePlace" :alwaysHideLabels="false" :showTitle="false"
                    v-model:pageIndex="currentPageIndex"/>
    </div>
  <div v-else>
    <EditViewSkeleton/>
  </div>
</template>

<style scoped>

</style>
